import { useEffect } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import { BRAND_ICON_KEYS, BrandGlyph } from "./brandGlyphs";
import { Icon } from "./icons";

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/* ---- Button ---- */
export type ButtonVariant = "primary" | "outline" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export function Button({
  variant = "primary",
  size = "md",
  block = false,
  loading = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  loading?: boolean;
}) {
  return (
    <button
      className={cx(
        "ax-btn",
        `ax-btn--${variant}`,
        size !== "md" && `ax-btn--${size}`,
        block && "ax-btn--block",
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading && <span className="ax-spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

/* ---- Card ---- */
export function Card({
  title,
  subtitle,
  flush = false,
  className,
  children,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  flush?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <section className={cx("ax-card", flush && "ax-card--flush", className)}>
      {title && <h3 className="ax-card__title">{title}</h3>}
      {subtitle && <p className="ax-card__sub">{subtitle}</p>}
      {children}
    </section>
  );
}

/* ---- Field + Input ---- */
export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="ax-field">
      {label && (
        <label className="ax-field__label" htmlFor={htmlFor}>
          {label}
        </label>
      )}
      {children}
      {error ? (
        <span className="ax-field__error">{error}</span>
      ) : (
        hint && <span className="ax-field__hint">{hint}</span>
      )}
    </div>
  );
}

export function Input({
  invalid = false,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return <input className={cx("ax-input", className)} aria-invalid={invalid || undefined} {...rest} />;
}

/* ---- Toggle ---- */
export function Toggle({
  checked,
  onChange,
  label,
  id,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  id?: string;
}) {
  return (
    <label className="ax-toggle" htmlFor={id}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="ax-toggle__track">
        <span className="ax-toggle__thumb" />
      </span>
      {label}
    </label>
  );
}

/* ---- Badge ---- */
export type BadgeTone = "neutral" | "good" | "warn" | "danger" | "accent";
export function Badge({ tone = "neutral", children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={cx("ax-badge", tone !== "neutral" && `ax-badge--${tone}`)}>{children}</span>;
}

/* ---- Spinner ---- */
export function Spinner({ label }: { label?: string }) {
  return <span className="ax-spinner" role="status" aria-label={label ?? "Loading"} />;
}

/* ---- Select ---- */
export function Select({
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx("ax-input", "ax-select", className)} {...rest}>
      {children}
    </select>
  );
}

/* ---- Avatar ---- */
export function Avatar({
  src,
  name,
  size = 32,
}: {
  src?: string | null;
  name?: string;
  size?: number;
}) {
  if (src) {
    return (
      <img
        className="ax-avatar"
        src={src}
        alt=""
        style={{ inlineSize: size, blockSize: size }}
      />
    );
  }
  const initials = (name ?? "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span
      className="ax-avatar ax-avatar--fallback"
      style={{ inlineSize: size, blockSize: size, fontSize: Math.round(size * 0.4) }}
    >
      {initials || "?"}
    </span>
  );
}

/* ---- Skeleton (loading placeholder) ---- */
export function Skeleton({
  width = "100%",
  height = 14,
  radius = "var(--radius-sm)",
}: {
  width?: number | string;
  height?: number | string;
  radius?: string;
}) {
  return (
    <span
      className="ax-skeleton"
      style={{ inlineSize: width, blockSize: height, borderRadius: radius }}
    />
  );
}

/* ---- Brand icon picker (modern SVG glyphs) ---- */
export function IconPicker({
  value,
  onChange,
}: {
  value?: string;
  onChange: (icon: string) => void;
}) {
  return (
    <div className="ax-iconpick" role="listbox">
      {BRAND_ICON_KEYS.map((key) => (
        <button
          type="button"
          key={key}
          role="option"
          aria-selected={value === key}
          className={value === key ? "ax-iconpick__i is-on" : "ax-iconpick__i"}
          onClick={() => onChange(key)}
        >
          <BrandGlyph id={key} size={20} />
        </button>
      ))}
    </div>
  );
}

/* ---- Drawer (side-sliding panel) ---- */
export function Drawer({
  open,
  onClose,
  side = "end",
  title,
  children,
  width = "min(380px, 92vw)",
}: {
  open: boolean;
  onClose: () => void;
  side?: "start" | "end";
  title?: ReactNode;
  children: ReactNode;
  width?: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="ax-drawer" role="dialog" aria-modal="true">
      <div className="ax-drawer__backdrop" onClick={onClose} />
      <aside className={`ax-drawer__panel ax-drawer__panel--${side}`} style={{ inlineSize: width }}>
        <div className="ax-drawer__head">
          <h3>{title}</h3>
          <button className="ax-modal__close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="ax-drawer__body">{children}</div>
      </aside>
    </div>
  );
}

/* ---- Modal (custom, themed — never a native browser dialog) ---- */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="ax-modal" role="dialog" aria-modal="true">
      <div className="ax-modal__backdrop" onClick={onClose} />
      <div className="ax-modal__card">
        <div className="ax-modal__head">
          <h3>{title}</h3>
          <button className="ax-modal__close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={16} />
          </button>
        </div>
        <div className="ax-modal__body">{children}</div>
        {footer && <div className="ax-modal__foot">{footer}</div>}
      </div>
    </div>
  );
}
